<div
  class="report-widget-renderer h-full min-h-0 w-full min-w-0"
  [attr.aria-busy]="loading"
  [class.report-widget-renderer--preview]="variant === 'preview'">
  <div *ngIf="loading && !data" class="rounded-md bg-white/80 p-5 text-sm text-slate-500 ring-1 ring-white/80">
    {{ 'SystemAdmin.Components.CustomReportEditor.Tab.Widget.Builder.PreviewLoading' | translate }}
  </div>

  <div *ngIf="!loading && errors.length" class="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
    <div *ngFor="let error of errors">{{ error }}</div>
  </div>

  <div *ngIf="!loading && !data" class="rounded-md border border-dashed border-slate-200 bg-white/70 p-5 text-sm text-slate-500">
    {{ 'SystemAdmin.Components.CustomReportEditor.Tab.Widget.Builder.NoColumns' | translate }}
  </div>

  <ng-template #widgetDecoration let-decoration>
    <span *ngIf="decorationType(decoration) === 'accentDot'" class="h-2 w-2 shrink-0 rounded-full" [ngStyle]="decorationStyle(decoration)"></span>
    <span *ngIf="decorationType(decoration) === 'accentBar'" class="h-2.5 w-10 shrink-0 rounded-full" [ngStyle]="decorationStyle(decoration)"></span>
    <fa-icon *ngIf="decorationType(decoration) === 'icon'" [icon]="decorationIconProp(decoration)" class="shrink-0" [ngStyle]="decorationStyle(decoration)"></fa-icon>
    <span *ngIf="decorationType(decoration) === 'badge' && decorationBadgeLabel(decoration)" class="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase" [ngStyle]="decorationStyle(decoration)">
      {{ decorationBadgeLabel(decoration) }}
    </span>
  </ng-template>

  <ng-container *ngIf="data">
    <div *ngIf="!hasData && !errors.length" class="flex h-full min-h-[5rem] items-center justify-center text-sm text-slate-400">
      {{ 'Dashboard.Home.DynamicWidgets.NoData' | translate }}
    </div>

    <app-report-widget-devextreme-chart-template
      *ngIf="isChart"
      class="block h-full min-h-0 min-w-0"
      [chartMetadata]="chartMetadata">
    </app-report-widget-devextreme-chart-template>

    <div *ngIf="!isChart && isKpi" class="grid min-h-0 gap-3 md:grid-cols-2">
      <div *ngFor="let item of summaryEntries" class="rounded-md border border-white/50 bg-white/70 px-4 py-3 ring-1 ring-white/60">
        <div class="truncate text-[10px] font-semibold uppercase tracking-wide text-indigo-500">{{ item.key }}</div>
        <div class="mt-1 truncate text-xl font-semibold text-indigo-950">{{ item.value }}</div>
      </div>
      <div *ngIf="!summaryEntries.length" class="rounded-md border border-dashed border-slate-200 bg-white/70 p-4 text-sm text-slate-500">
        {{ 'SystemAdmin.Components.CustomReportEditor.Tab.Widget.Builder.NoColumns' | translate }}
      </div>
    </div>

    <div
      *ngIf="!isChart && !isKpi && rows.length && isCompactTable && !displayHeaders.length"
      class="flex h-full min-h-[5rem] items-center justify-center rounded-md border border-dashed border-slate-200 bg-white/50 px-4 py-3 text-sm text-slate-500">
      {{ 'SystemAdmin.Components.CustomReportEditor.Tab.Widget.Builder.NoSelectedColumns' | translate }}
    </div>

    <div
      *ngIf="!isChart && !isKpi && rows.length && !isCompactTable && !hasRenderableSlots"
      class="flex h-full min-h-[5rem] items-center justify-center rounded-md border border-dashed border-slate-200 bg-white/50 px-4 py-3 text-sm text-slate-500">
      {{ 'SystemAdmin.Components.CustomReportEditor.Tab.Widget.Builder.NoSlotAssignments' | translate }}
    </div>

    <div *ngIf="!isChart && !isKpi && rows.length && isCompactTable && displayHeaders.length" class="min-w-full overflow-hidden rounded-md bg-white/90 ring-1 ring-white/80">
      <table class="w-full text-left text-xs">
        <thead class="bg-slate-50 text-slate-400">
          <tr>
            <th *ngFor="let header of displayHeaders; trackBy: trackHeader" class="px-3 py-2 font-semibold uppercase tracking-wide">
              <span class="truncate">{{ header }}</span>
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100">
          <tr *ngFor="let row of rows" class="bg-white" [ngStyle]="rowStyle(row)">
            <td
              *ngFor="let header of displayHeaders; trackBy: trackHeader"
              class="max-w-[180px] px-3 py-2 font-semibold text-slate-700"
              [ngStyle]="cellStyle(row, header)">
              <span class="inline-flex max-w-full items-center gap-1.5">
                <ng-container *ngFor="let decoration of cellLeftDecorations(row, header); trackBy: trackDecoration" [ngTemplateOutlet]="widgetDecoration" [ngTemplateOutletContext]="{ $implicit: decoration }"></ng-container>
                <fa-icon *ngIf="cellIconName(row, header)" [icon]="cellIconProp(row, header)" class="shrink-0" [ngStyle]="cellIconStyle(row, header)"></fa-icon>
                <span class="min-w-0 truncate">{{ displayCell(row, header) }}</span>
                <span *ngIf="cellBadgeLabel(row, header) as cellBadge" class="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase" [ngStyle]="cellBadgeStyle(row, header)">
                  {{ cellBadge }}
                </span>
                <ng-container *ngFor="let decoration of cellRightDecorations(row, header); trackBy: trackDecoration" [ngTemplateOutlet]="widgetDecoration" [ngTemplateOutletContext]="{ $implicit: decoration }"></ng-container>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <app-report-widget-top-one-spotlight-template
      *ngIf="!isChart && !isKpi && rows.length && hasRenderableSlots && isTopOne"
      class="block h-full min-h-0 min-w-0"
      [rows]="rows"
      [config]="config"
      [api]="templateApi">
    </app-report-widget-top-one-spotlight-template>

    <app-report-widget-ranked5list-template
      *ngIf="!isChart && !isKpi && rows.length && hasRenderableSlots && isTopList && !isTopOne"
      class="block h-full min-h-0 min-w-0"
      [rows]="rows"
      [config]="config"
      [api]="templateApi"
      [highlightFirst]="highlightFirst">
    </app-report-widget-ranked5list-template>

    <div *ngIf="!isChart && !isKpi && rows.length && hasRenderableSlots && !isTopOne && !isTopList" class="grid min-h-0 gap-2 overflow-auto">
      <div
        *ngFor="let row of rows; let rowIndex = index"
        class="rounded-md border border-white/50 px-3 py-2"
        [ngStyle]="rowStyle(row)"
        [ngClass]="rowIndex === 0 && highlightFirst ? 'border-emerald-300/60 bg-emerald-50/40' : 'border-white/35 bg-white/70'">
        <div class="mb-1 flex items-center justify-between gap-3">
          <div class="flex min-w-0 items-center gap-2">
            <span class="text-xs font-semibold text-slate-500">#{{ rowIndex + 1 }}</span>
            <fa-icon *ngIf="iconName(row)" [icon]="iconProp(row)" [ngStyle]="iconStyle(row)"></fa-icon>
            <span *ngIf="badgeLabel(row) as badge" class="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase" [ngStyle]="badgeStyle(row)">
              {{ badge }}
            </span>
          </div>
          <span *ngIf="rowIndex === 0 && highlightFirst" class="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
            {{ 'Dashboard.Home.DynamicWidgets.Highlighted' | translate }}
          </span>
        </div>

        <div class="grid grid-cols-2 gap-2 text-xs md:grid-cols-4">
          <div *ngFor="let slot of slotDefinitions; trackBy: trackSlot" [hidden]="slot.role === 'color' || !slotValue(row, slot.key)" class="min-w-0">
            <div *ngIf="slotLabelsVisible" class="truncate text-slate-400">{{ slotColumnLabel(slot.key) }}</div>
            <div class="flex min-w-0 items-center gap-1.5 font-semibold text-slate-800" [ngStyle]="slotCellStyle(row, slot.key)">
              <ng-container *ngFor="let decoration of slotLeftDecorations(row, slot.key); trackBy: trackDecoration" [ngTemplateOutlet]="widgetDecoration" [ngTemplateOutletContext]="{ $implicit: decoration }"></ng-container>
              <fa-icon *ngIf="slotIconName(row, slot.key)" [icon]="slotIconProp(row, slot.key)" class="shrink-0" [ngStyle]="slotIconStyle(row, slot.key)"></fa-icon>
              <span class="min-w-0 truncate">{{ slotValue(row, slot.key) }}</span>
              <span *ngIf="slotBadgeLabel(row, slot.key) as slotBadge" class="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase" [ngStyle]="slotBadgeStyle(row, slot.key)">
                {{ slotBadge }}
              </span>
              <ng-container *ngFor="let decoration of slotRightDecorations(row, slot.key); trackBy: trackDecoration" [ngTemplateOutlet]="widgetDecoration" [ngTemplateOutletContext]="{ $implicit: decoration }"></ng-container>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="!isChart && !isKpi && !rows.length && !errors.length" class="rounded-md border border-dashed border-slate-200 bg-white/70 p-4 text-sm text-slate-500">
      {{ 'SystemAdmin.Components.CustomReportEditor.Tab.Widget.Builder.NoColumns' | translate }}
    </div>
  </ng-container>
</div>
